import { Component, type ReactNode } from "react"

import { Button } from "@/components/ui/button"
import { translate } from "@/i18n"

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

/** Catches render errors anywhere below and shows a recoverable screen instead of a blank page. */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error) {
    console.error("Unhandled render error:", error)
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex h-screen flex-col items-center justify-center gap-3 p-6 text-center">
          <h1 className="text-lg font-semibold">{translate("error.somethingWentWrong")}</h1>
          <p className="max-w-md text-sm text-muted-foreground">{this.state.error.message}</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            {translate("error.reload")}
          </Button>
        </div>
      )
    }
    return this.props.children
  }
}
